import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

import { API } from '@/utils/api';

export default function DeleteAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/delete-account/${token}`);
        setValid(true);
        setEmail(r.data.email || '');
      } catch {
        setValid(false);
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const handleConfirmDelete = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/auth/delete-account/${token}`);
      setDone(true);
      toast.success('Konto zostało usunięte.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie udało się usunąć konta.');
    } finally {
      setConfirming(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" data-testid="delete-account-loading" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link wygasł lub jest nieprawidłowy</h1>
          <p className="text-slate-600 mb-6">Jeśli masz konto i chcesz je usunąć, zaloguj się i zrób to z poziomu ustawień.</p>
          <Link to="/login">
            <Button data-testid="delete-account-go-login" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Przejdź do logowania
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Konto zostało usunięte</h1>
          <p className="text-slate-600 mb-6">Wszystkie Twoje dane zostały zanonimizowane. Nie wyślemy Ci więcej żadnych wiadomości.</p>
          <Link to="/">
            <Button data-testid="delete-account-done-home" className="bg-slate-900 hover:bg-slate-800 text-white">
              Wróć na stronę główną
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <span className="font-extrabold text-xl text-emerald-600">ApisAI</span>
          </Link>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-start gap-3 mb-5">
            <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-1" />
            <div>
              <h1 className="text-xl font-bold text-slate-900 mb-1">Usuń swoje konto</h1>
              <p className="text-sm text-slate-600 leading-relaxed">
                Chcesz nieodwracalnie usunąć konto powiązane z adresem:
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-5">
            <p className="text-sm font-mono text-slate-900 break-all" data-testid="delete-account-email">{email}</p>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg mb-6">
            <p className="text-xs text-amber-900 leading-relaxed">
              <strong>Uwaga:</strong> Po usunięciu konta nie będzie możliwości jego przywrócenia.
              Twoje dane zostaną zanonimizowane zgodnie z RODO. Nie wyślemy Ci więcej żadnych wiadomości.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              data-testid="delete-account-confirm"
              onClick={handleConfirmDelete}
              disabled={confirming}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3"
            >
              {confirming ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Tak, usuń moje konto'}
            </Button>
            <Link to="/">
              <Button
                data-testid="delete-account-cancel"
                variant="outline"
                className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Anuluj
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
